import * as THREE from 'three'

export type EarringStyle = 'stud' | 'drop' | 'hoop' | 'jhumka'

export interface ProceduralEarringOptions {
  style: EarringStyle
  /** Metal colour. */
  metal: number | string
  /** Accent (pearl/stone) colour. */
  accent: number | string
  /** Overall size multiplier. */
  size: number
}

export const EARRING_DEFAULTS: ProceduralEarringOptions = {
  style: 'drop',
  metal: 0xd4a94a,
  accent: 0xf6f1e7,
  size: 1,
}

export function metalMaterial(color: number | string): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: new THREE.Color(color), metalness: 1, roughness: 0.25, envMapIntensity: 1.1 })
}

export function pearlMaterial(color: number | string): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(color),
    metalness: 0,
    roughness: 0.35,
    clearcoat: 0.9,
    clearcoatRoughness: 0.25,
    envMapIntensity: 0.7,
  })
}

/**
 * Procedural earrings in centimetres. Origin = the lobe attachment point (piercing);
 * the jewellery hangs along −Y. Returns { group, hangLengthCm } — hangLength is the
 * distance from origin to the centre of mass, used as the pendulum length.
 */
export function createProceduralEarring(options: Partial<ProceduralEarringOptions> = {}): {
  group: THREE.Group
  hangLengthCm: number
} {
  const o = { ...EARRING_DEFAULTS, ...options }
  const s = o.size
  const group = new THREE.Group()
  group.name = `procedural-earring-${o.style}`
  const metal = metalMaterial(o.metal)
  const accent = pearlMaterial(o.accent)
  let hangLengthCm = 0.4 * s

  switch (o.style) {
    case 'stud': {
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.32 * s, 20, 14), accent)
      head.name = 'stud_head'
      head.position.set(0, 0, 0.12 * s)
      const rim = new THREE.Mesh(new THREE.TorusGeometry(0.3 * s, 0.05 * s, 8, 24), metal)
      rim.name = 'stud_rim'
      rim.position.z = 0.08 * s
      group.add(head, rim)
      hangLengthCm = 0.3 * s
      break
    }
    case 'hoop': {
      const r = 1.1 * s
      const hoop = new THREE.Mesh(new THREE.TorusGeometry(r, 0.07 * s, 10, 40), metal)
      hoop.name = 'hoop'
      // torus in XY plane; hang from its top point → shift down so origin is the top
      hoop.position.y = -r
      group.add(hoop)
      hangLengthCm = r
      break
    }
    case 'jhumka': {
      // stud + short thick link, dome right under the lobe (no floating gap)
      const stud = new THREE.Mesh(new THREE.SphereGeometry(0.22 * s, 14, 10), metal)
      stud.name = 'stud'
      const link = new THREE.Mesh(new THREE.CylinderGeometry(0.07 * s, 0.07 * s, 0.45 * s, 10), metal)
      link.name = 'link'
      link.position.y = -0.25 * s
      const dome = new THREE.Mesh(new THREE.ConeGeometry(1.05 * s, 1.25 * s, 28, 1, true), metal)
      dome.name = 'dome'
      dome.position.y = -1.05 * s
      const beadRing = new THREE.Group()
      beadRing.name = 'bead_ring'
      const beadGeo = new THREE.SphereGeometry(0.13 * s, 10, 8)
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2
        const bead = new THREE.Mesh(beadGeo, accent)
        bead.position.set(Math.cos(a) * 0.98 * s, -1.75 * s, Math.sin(a) * 0.98 * s)
        beadRing.add(bead)
      }
      group.add(stud, link, dome, beadRing)
      hangLengthCm = 1.3 * s
      break
    }
    case 'drop':
    default: {
      const wire = new THREE.Mesh(new THREE.CylinderGeometry(0.035 * s, 0.035 * s, 1.6 * s, 8), metal)
      wire.name = 'wire'
      wire.position.y = -0.8 * s
      const pearl = new THREE.Mesh(new THREE.SphereGeometry(0.45 * s, 22, 16), accent)
      pearl.name = 'pearl'
      pearl.position.y = -2.0 * s
      const cap = new THREE.Mesh(new THREE.ConeGeometry(0.2 * s, 0.25 * s, 12), metal)
      cap.name = 'cap'
      cap.position.y = -1.55 * s
      group.add(wire, cap, pearl)
      hangLengthCm = 2.0 * s
      break
    }
  }
  return { group, hangLengthCm }
}
